import "./ParsedProjectsTable.css";

import { UploadBlueIcon } from "../../../assets";
import { ParsedRowError, ProjectRow } from "../../../models";
import { ResultData } from "../../../service/Shared";
import { ErrorBox } from "../../cards";
import { DataGrid, DataGridColumnDefinition } from "../../data";
import { Button } from "../../forms";
import { LoadingSpinner } from "../../general";
import { Card, Column, Row } from "../../layout";
import { ParsedProjectsTableProps } from "./models";

const projectColumns: DataGridColumnDefinition[] = [
  { key: "displayName", name: "Project name", dataType: "string", minWidth: 220 },
  { key: "projectTypeName", name: "Project type", dataType: "string", minWidth: 180 },
  { key: "standardName", name: "Standard", dataType: "string", minWidth: 200 },
  { key: "verifierName", name: "VVB", dataType: "string", minWidth: 200 },
  { key: "addressRegion", name: "Region", dataType: "string", minWidth: 170 },
  { key: "addressPostcode", name: "Postcode", dataType: "string", minWidth: 130 },
  { key: "gridReference", name: "Grid reference", dataType: "string", minWidth: 150 },
  { key: "latitude", name: "Latitude", dataType: "string", minWidth: 120 },
  { key: "longitude", name: "Longitude", dataType: "string", minWidth: 120 },
  { key: "addressCountryCode", name: "Country", dataType: "string", formatter: "countryByCode", minWidth: 150 },
  { key: "areaNetHa", name: "Area (ha)", dataType: "number", formatter: "number", minWidth: 120, width: 140 },
];

const landholdingColumns: DataGridColumnDefinition[] = [
  { key: "landholdingName", name: "Landholding", dataType: "string", minWidth: 200 },
  { key: "landholdingReference", name: "Landholding reference", dataType: "string", minWidth: 200 },
  { key: "landholdingReferenceId", name: "Landholding reference ID", dataType: "string", minWidth: 200 },
];

const toResultData = (row: ProjectRow): ResultData => ({
  uuid: row.key,
  displayName: row.displayName,
  projectTypeName: row.projectTypeName ?? null,
  standardName: row.standardName ?? null,
  verifierName: row.verifierName ?? null,
  addressRegion: row.addressRegion,
  addressPostcode: row.addressPostcode,
  gridReference: row.gridReference,
  latitude: row.latitude,
  longitude: row.longitude,
  addressCountryCode: row.addressCountryCode,
  areaNetHa: row.areaNetHa,
  landholdingName: row.landholdingName ?? null,
  landholdingReference: row.landholdingReference ?? null,
  landholdingReferenceId: row.landholdingReferenceId ?? null,
});

const hasLandholding = (row: ProjectRow): boolean =>
  !!row.landholdingName || !!row.landholdingReference || !!row.landholdingReferenceId;

interface RowErrorGroup {
  key: string;
  rowNumber: number;
  label: string;
  reasons: string[];
}

// One group per project (or landholding) in sheet order, so the user can work down the file fixing
// each. Keyed on row and label together: a landholding's own failure is stamped onto one of its
// project rows, and must not be filed under that project's name.
const groupRowErrors = (errors: ParsedRowError[]): RowErrorGroup[] => {
  const groups = new Map<string, RowErrorGroup>();
  errors.forEach((error) => {
    const label = error.label ?? `Row ${error.rowNumber}`;
    const key = `${error.rowNumber}|${label}`;
    const group = groups.get(key) ?? { key, rowNumber: error.rowNumber, label, reasons: [] };
    if (!group.reasons.includes(error.reason)) group.reasons.push(error.reason);
    groups.set(key, group);
  });

  return Array.from(groups.values()).sort((a, b) => a.rowNumber - b.rowNumber);
};

export const ParsedProjectsTable = ({
  rows,
  rowErrors,
  onReplaceFile,
  isLoading = false,
  loadingMessage = "Processing your projects. This can take a moment for a large file.",
  disabled = false,
  corners = "default",
  variant = "default",
}: ParsedProjectsTableProps): JSX.Element => {
  const isLandholdingUpload = rows.some(hasLandholding);
  const columns = isLandholdingUpload
    ? [projectColumns[0], ...landholdingColumns, ...projectColumns.slice(1)]
    : projectColumns;
  const rowErrorGroups = groupRowErrors(rowErrors ?? []);

  return (
    <Card corners={corners}>
      <div className="ParsedProjectsTable">
        <Row spacingV="ml">
          <Column span={12}>
            <div className="ParsedProjectsTable_header">
              <div>
                <h2>Projects from your file</h2>
                <span className="body1">
                  {isLandholdingUpload
                    ? "Each project is shown with the landholding it belongs to."
                    : "Check the projects below before continuing."}
                </span>
              </div>
              {!disabled && (
                <Button
                  variant="secondary"
                  text="Replace file"
                  icon={<UploadBlueIcon />}
                  corners={corners}
                  disabled={isLoading}
                  onClick={onReplaceFile}
                />
              )}
            </div>
          </Column>
        </Row>

        {rowErrorGroups.length > 0 && (
          <Row spacingV="ml">
            <Column span={12}>
              <ErrorBox title="Some projects in your file need fixing">
                <div className="ParsedProjectsTable_rowErrors">
                  {rowErrorGroups.map(({ key, label, reasons }) => (
                    <div key={key}>
                      <p className="body2 ParsedProjectsTable_rowErrorHeading">{label}</p>
                      <ul>
                        {reasons.map((reason) => (
                          <li key={reason} className="body2">
                            {reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  <p className="body2">Please change the file and reupload with the correct details.</p>
                </div>
              </ErrorBox>
            </Column>
          </Row>
        )}

        {/* The grid keeps its rows while the request runs, and the Next button's own spinner is
            small and far below a long table - so the wait is said in words, at the top. */}
        {isLoading && (
          <Row spacingV="ml">
            <Column span={12}>
              <div className="ParsedProjectsTable_loading" role="status" aria-live="polite">
                <LoadingSpinner type="blue" size="medium" />
                <span className="body1">{loadingMessage}</span>
              </div>
            </Column>
          </Row>
        )}

        <Row>
          <Column span={12}>
            <DataGrid
              columns={columns}
              // In file order, never sorted: the person checking the table has the file open
              // beside it, and the same order is what lets them find a row in both.
              data={rows.map((row) => toResultData(row))}
              clientSide
              // Every row in the file is shown at once - paging a one-off import hides rows the
              // user is being asked to check. `pageable` only hides the pager; the client-side slice
              // still uses `pageSize`, so it has to cover the whole file too.
              pageable={false}
              pagination={{ currentPage: 1, pageSize: rows.length }}
              filterable={false}
              noDataMessage="No projects to display"
              dataIsLoading={isLoading}
              variant={variant}
            />
          </Column>
        </Row>
      </div>
    </Card>
  );
};
